import React, { useEffect, useMemo } from "react";
import { useAuth } from "../../../../../context/AuthContext";
import { ProjectInfoFormStyled } from "./project-info-form.style";
import ScheduleField from "../../../../../components/form-fields/schedule-field/schedule-field";
import SelectFormField from "../../../../../components/form-fields/select-field/select-form-field";
import { Typography } from "@mui/material";
import DatePickerFormField from "../../../../../components/form-fields/date-picker/date-picker-form-field";
import { TextInputFormField } from "../../../../../components/form-fields/text-input-field/text-input-form-field";
import { PROJECT_STATES_OPTIONS } from "../../../../../constants/constants";
import { DollarIcon } from "../../../../../utils/svg-components";

export const Project_INFO_FORM_VIEW_TYPES = {
  detail: "detail",
  creation: "creation",
  creationGroup: "creationGroup",
  groupDetail: "groupDetail",
};

const ProjectInfoForm = ({
  form,
  view = "creation",
  clientOptions = [],
  clientContactOptions = [],
  projectLeadOptions = [],
  showStateAndSource = true,
  projectGroupOptions = [],
  isClientContactDisabled = true,
}) => {
  const { control, getValues, watch, setValue } = form;
  watch();
  const { userPermissions } = useAuth();
  const formData = getValues();
  const isGrouped = getValues()?.isGrouped;
  const isBillable = !getValues()?.isNonBillable;

  const isDetailedView = view === Project_INFO_FORM_VIEW_TYPES?.detail;
  const isGroupDetailView = view === Project_INFO_FORM_VIEW_TYPES.groupDetail;
  const isCreationView = view === Project_INFO_FORM_VIEW_TYPES.creation;
  const isCreationGroupView =
    view === Project_INFO_FORM_VIEW_TYPES.creationGroup;
  const isGeneralUser = userPermissions === "General";

  const addToExistingProject = watch("addToExistingProject");
  const modifiedProjectGroupOptions = [
    { label: "Select", value: "" }, // Default empty option
    ...projectGroupOptions, // Spread existing options
  ];

  // Sort clientOptions alphabetically
  const sortedClientOptions = useMemo(() => {
    return [...clientOptions].sort((a, b) => a.label.localeCompare(b.label));
  }, [clientOptions]);

  // Disable the other option if one is selected
  useEffect(() => {
    if (isGrouped) {
      setValue("addToExistingProject", "", { shouldValidate: true });
    } else if (addToExistingProject) {
      setValue("isGrouped", false, { shouldValidate: true });
    }
  }, [isGrouped, addToExistingProject, setValue]);

  // Use watch to subscribe to changes in the addToExistingProject field
  const selectedProjectGroup = watch("addToExistingProject");

  // Use watch to subscribe to changes in the schedule
  const schedule = formData?.schedule;

  const totalServiceValue = schedule?.reduce((total, current) => {
    // Safely parse the value, stripping out commas and handling invalid numbers gracefully
    const value =
      parseFloat(current?.value?.toString().replace(/,/g, "").trim()) || 0;
    return total + value;
  }, 0);

  useEffect(() => {
    // Allow serviceValue to be set to 0 if totalServiceValue is 0
    setValue("serviceValue", totalServiceValue?.toFixed(2), {
      shouldValidate: true,
    });
  }, [setValue, totalServiceValue]);

  useEffect(() => {
    if (isCreationGroupView) {
      setValue("isGrouped", true, { shouldValidate: true });
    }
  }, [isCreationGroupView, setValue]);

  useEffect(() => {
    if (isGroupDetailView) {
      // Use pre-existing serviceValue or compute from another source
      const serviceValue = formData?.serviceValue || "0.00";
      setValue("serviceValue", serviceValue, { shouldValidate: true });
    }
  }, [isGroupDetailView, formData?.serviceValue, setValue]);

  useEffect(() => {
    if (isDetailedView) {
      // Use pre-existing serviceValue or compute from another source
      const serviceValue = formData?.serviceValue || "0.00";
      setValue("serviceValue", serviceValue, { shouldValidate: true });
    }
  }, [isDetailedView, formData?.serviceValue, setValue]);

  const parsedProjectLeadOptions = projectLeadOptions
    ?.filter((x) => x?.employmentStatus === "Active")
    .sort((a, b) =>
      a.label.localeCompare(b.label, "en", {
        sensitivity: "base",
      })
    );

  const renderFieldsBasedOnView = () => {
    if (isCreationView) {
      return (
        <ProjectInfoFormStyled isCreationView={isCreationView}>
          <div className="project-cont">
            <div className="grid-2">
              <TextInputFormField
                isRequired
                label={isGrouped ? "Project Group Name" : "Project Name"}
                name={"projectName"}
                control={control}
                defaultValue={formData?.projectName}
              />
              <SelectFormField
                control={control}
                name="addToExistingProject"
                label="Add to Existing Project Group"
                placeholder="Select Project Group"
                options={modifiedProjectGroupOptions}
                disabled={isGrouped}
                customOnChange={(event) => {
                  const newVal = event.target.value;

                  const selectedGroup = projectGroupOptions.find(
                    (group) => group.value === newVal
                  );

                  if (selectedGroup) {
                    const clientOption = sortedClientOptions.find(
                      (client) => client.value === selectedGroup.clientId
                    );
                    if (clientOption) {
                      setValue("client", clientOption.value, {
                        shouldValidate: true,
                      });
                    }
                  }
                }}
              />
            </div>
            <div className="grid-3">
              <SelectFormField
                name="client"
                label="Client"
                control={control}
                placeholder="Please Select"
                options={sortedClientOptions}
                isRequired={!selectedProjectGroup}
                disabled={!!selectedProjectGroup}
              />
              <SelectFormField
                // isRequired
                name="contact"
                label="Client Contact"
                control={control}
                placeholder="Please Select"
                options={clientContactOptions}
                disabled={isDetailedView}
              />

              <SelectFormField
                isRequired
                name="projectLead"
                label="Project Lead"
                control={control}
                placeholder="Please Select"
                options={parsedProjectLeadOptions}
              />
            </div>
            <div className="grid-3">
              {showStateAndSource && (
                <SelectFormField
                  isRequired
                  control={control}
                  name="state"
                  label="State"
                  placeholder="Please Select"
                  disabled={isGrouped}
                  options={PROJECT_STATES_OPTIONS}
                />
              )}

              {showStateAndSource && (
                <SelectFormField
                  control={control}
                  name="source"
                  label="Source"
                  placeholder="Please Select"
                  options={[
                    { label: "RFP", value: "rfp" },
                    { label: "New Lead", value: "newLead" },
                    { label: "New Work", value: "newWork" },
                  ]}
                  disabled={isGrouped}
                />
              )}

              <DatePickerFormField
                isRequired={!isGrouped}
                label="Start Date (mm/dd/yyyy)"
                placeholder={"Start"}
                name="start"
                size={"max"}
                control={control}
                // disableFn={"past"}
                disabled={isGrouped}
                description={
                  "The Start Date determines Month 1 in the Forecasted Service Revenue Schedule."
                }
              />
            </div>
            <hr className="hr-2"></hr>
            <div className="grid-3">
              <SelectFormField
                isRequired
                name="isNonBillable"
                label="Service Type"
                control={control}
                placeholder="Please Select"
                options={[
                  { label: "Non Billable", value: true },
                  { label: "Billable", value: false },
                ]}
                disabled={isGrouped}
              />
              <SelectFormField
                isRequired={!isGrouped && isBillable}
                name="probability"
                label="Probability"
                control={control}
                placeholder="Please Select"
                options={[
                  { label: "100%", value: "100" },
                  { label: "80%", value: "80" },
                  { label: "60%", value: "60" },
                  { label: "40%", value: "40" },
                  { label: "20%", value: "20" },
                ]}
                disabled={isGrouped || !isBillable}
              />

              <TextInputFormField
                readOnly={!isGrouped || !isBillable}
                description={
                  isGrouped ? "Total projected service and material value." : ""
                }
                label={
                  isGrouped
                    ? "Total Project Group Value"
                    : "Total Forecasted Service Revenue"
                }
                name={"serviceValue"}
                control={control}
                inputType={"text"}
                InputProps={{
                  startAdornment: <div>$</div>,
                }}
                disabled={!isGrouped || !isBillable}
                isTransparent={!isGrouped || !isBillable}
                className="purpleBg"
              />
            </div>
            <div className="schedulePadding">
              <ScheduleField
                // isRequired={!isGrouped && isBillable}
                name={"schedule"}
                description={
                  "Month One is determined by the month specified under Start Date."
                }
                control={control}
                label={"Forecasted Service Revenue Schedule"}
                onBlur={() => {
                  const schedule = getValues("schedule");
                  const totalServiceValue = schedule.reduce(
                    (total, current) => {
                      const value = parseFloat(current.value) || 0;
                      return total + value;
                    },
                    0
                  );
                  setValue("serviceValue", totalServiceValue.toFixed(2));
                }}
                isAutoFocusEnabled={form?.formState?.isDirty}
                disabled={isGrouped || !isBillable}
                canAddFields={!isGrouped}
                isTransparent={getValues().isNonBillable}
              />
            </div>

            <div className="row-style">
              <TextInputFormField
                // isRequired
                multiline
                rows={5}
                label={isGrouped ? "Project Group Notes" : "Project Notes"}
                name={"projectDescription"}
                control={control}
                description={
                  "Notes are for internal use only and will not appear on outward-facing materials."
                }
              />
            </div>
          </div>
        </ProjectInfoFormStyled>
      );
    }

    if (isDetailedView) {
      return (
        <ProjectInfoFormStyled isDetailedView={isDetailedView}>
          <Typography variant="h6">Details</Typography>

          <div className="grid-template">
            <div className="left-content">
              <div className="flex">
                <TextInputFormField
                  isRequired
                  label={isGrouped ? "Project Group Name" : "Project Name"}
                  name={"projectName"}
                  control={control}
                  defaultValue={formData?.projectName}
                />
                <SelectFormField
                  control={control}
                  name="addToExistingProject"
                  label="Add to Existing Project Group"
                  placeholder="Select Project Group"
                  options={modifiedProjectGroupOptions}
                  disabled={isGrouped}
                  customOnChange={(event) => {
                    const newVal = event.target.value;

                    const selectedGroup = projectGroupOptions.find(
                      (group) => group.value === newVal
                    );

                    if (selectedGroup) {
                      const clientOption = sortedClientOptions.find(
                        (client) => client.value === selectedGroup.clientId
                      );
                      if (clientOption) {
                        setValue("client", clientOption.value, {
                          shouldValidate: true,
                        });
                      }
                    }
                  }}
                />
              </div>
              <div className="flex-grid">
                <SelectFormField
                  name="client"
                  label="Client"
                  control={control}
                  placeholder="Please Select"
                  options={sortedClientOptions}
                  isRequired={!selectedProjectGroup}
                  disabled={!!selectedProjectGroup || !!formData?.id}
                  // disabled={isDetailedView}
                />
                <SelectFormField
                  name="contact"
                  label="Client Contact"
                  control={control}
                  // isRequired
                  placeholder="Please Select"
                  options={clientContactOptions}
                  disabled={isClientContactDisabled}
                />
                <SelectFormField
                  isRequired
                  name="projectLead"
                  label={isGrouped ? "Project Group Lead" : "Project Lead"}
                  control={control}
                  placeholder="Please Select"
                  options={parsedProjectLeadOptions}
                />
              </div>
              <div className="flex-grid">
                <SelectFormField
                  isRequired
                  control={control}
                  name="state"
                  label="State"
                  placeholder="Please Select"
                  disabled={isGrouped}
                  options={PROJECT_STATES_OPTIONS}
                />

                <SelectFormField
                  control={control}
                  name="source"
                  label={"Source"}
                  disabled={isGeneralUser}
                  options={[
                    { label: "RFP", value: "rfp" },
                    { label: "New Lead", value: "newLead" },
                    { label: "New Work", value: "newWork" },
                  ]}
                />
                <DatePickerFormField
                  isRequired={!isGrouped}
                  label="Start Date (mm/dd/yyyy)"
                  placeholder={"Start"}
                  name="start"
                  size={"max"}
                  control={control}
                  // disableFn={"past"}
                  disabled={isGrouped}
                  description={
                    "The Start Date determines Month 1 in the Forecasted Service Revenue Schedule."
                  }
                />
              </div>
              <span className="hr"></span>
              <div className="flex-grid">
                <SelectFormField
                  isRequired
                  name="isNonBillable"
                  label="Service Type"
                  control={control}
                  placeholder="Please Select"
                  options={[
                    { label: "Non Billable", value: true },
                    { label: "Billable", value: false },
                  ]}
                  disabled={isGrouped}
                />
                <SelectFormField
                  isRequired={!isGrouped && isBillable}
                  name="probability"
                  label="Probability"
                  control={control}
                  placeholder="Please Select"
                  options={[
                    { label: "100%", value: "100" },
                    { label: "80%", value: "80" },
                    { label: "60%", value: "60" },
                    { label: "40%", value: "40" },
                    { label: "20%", value: "20" },
                  ]}
                  disabled={isGrouped || !isBillable}
                />

                <TextInputFormField
                  readOnly={!isGrouped || !isBillable}
                  description={
                    isGrouped
                      ? "Total projected service and material value."
                      : ""
                  }
                  label={
                    isGrouped
                      ? "Total Project Group Value"
                      : "Total Forecasted Service Revenue"
                  }
                  name={"serviceValue"}
                  // value={formatNumber(getValues("serviceValue"))}
                  value={formData?.serviceValue || ""}
                  control={control}
                  inputType={"text"}
                  InputProps={{
                    startAdornment: <div>$</div>,
                  }}
                  disabled={!isGrouped || !isBillable}
                  isTransparent={!isGrouped || !isBillable}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/,/g, ""); // Remove commas
                    const parsedValue = parseFloat(rawValue) || 0; // Parse as number
                    setValue("serviceValue", parsedValue.toFixed(2), {
                      shouldValidate: true,
                    }); // Update state
                  }}
                  sx={{
                    backgroundColor: "#DEDAFB", // Blue background color
                    color: "white",
                    borderRadius: "4px",
                  }}
                />
              </div>
              <ScheduleField
                isRequired={!isGrouped && isBillable}
                name={"schedule"}
                description={
                  "Month One is determined by the month specified under Start Date."
                }
                control={control}
                label={"Forecasted Service Revenue Schedule"}
                onBlur={() => {
                  const schedule = getValues("schedule");
                  const totalServiceValue = schedule.reduce(
                    (total, current) => {
                      const value =
                        parseFloat(current.value.replace(/,/g, "")) || 0;
                      return total + value;
                    },
                    0
                  );
                  setValue("serviceValue", totalServiceValue.toFixed(2), {
                    shouldValidate: true,
                  });
                }}
                isAutoFocusEnabled={form?.formState?.isDirty}
                disabled={isGrouped || !isBillable}
                canAddFields={!isGrouped}
                isTransparent={getValues().isNonBillable}
              />
            </div>
            <div className="notes">
              <TextInputFormField
                multiline
                rows={20}
                label={"Project Notes"}
                name={"projectDescription"}
                control={control}
                description={
                  "Notes are for internal use only and will not appear on outward-facing materials."
                }
              />
            </div>
          </div>
          {!isGrouped && isBillable && (
            <div className="plans">
              <Typography variant="h6"> Invoice Information</Typography>
              <div className="invoice">
                <TextInputFormField
                  label="Invoiced Service Value"
                  name={"invoiceValue"}
                  control={control}
                  InputProps={{ startAdornment: <DollarIcon /> }}
                  description="Total value of invoiced services to date."
                  type="number"
                />
                <SelectFormField
                  label="Invoice Type"
                  name={"invoiceType"}
                  control={control}
                  options={[
                    { label: "Interim", value: "interim" },
                    { label: "Final", value: "final" },
                  ]}
                />
                <DatePickerFormField
                  label="Last Updated (mm/dd/yyyy)"
                  placeholder={"Updated"}
                  name={"invoiceDate"}
                  size={"max"}
                  control={control}
                />
              </div>
            </div>
          )}
        </ProjectInfoFormStyled>
      );
    }

    if (isCreationGroupView) {
      return (
        <ProjectInfoFormStyled isCreationView={isCreationView}>
          <div className="project-cont">
            <div className="grid-2">
              <TextInputFormField
                isRequired
                label="Project Group Name"
                name={"projectName"}
                control={control}
                defaultValue={formData?.projectName}
              />
              <TextInputFormField
                readOnly={!isGrouped || !isBillable}
                description="Total projected service and material value."
                label="Total Project Group Value"
                name={"serviceValue"}
                control={control}
                inputType={"text"}
                InputProps={{
                  startAdornment: <div>$</div>,
                }}
                disabled={!isGrouped || !isBillable}
                isTransparent={!isGrouped || !isBillable}
              />
            </div>
            <div className="grid-3">
              <SelectFormField
                name="client"
                label="Client"
                control={control}
                isRequired={!selectedProjectGroup}
                placeholder="Please Select"
                options={sortedClientOptions}
                disabled={!!selectedProjectGroup}
              />
              <SelectFormField
                // isRequired
                name="contact"
                label="Client Contact"
                control={control}
                placeholder="Please Select"
                options={clientContactOptions}
                disabled={isDetailedView}
              />

              <SelectFormField
                isRequired
                name="projectLead"
                label="Group Lead"
                control={control}
                placeholder="Please Select"
                options={parsedProjectLeadOptions}
              />
            </div>
            <hr className="hr-2"></hr>
            <div>
              <TextInputFormField
                // isRequired
                multiline
                rows={5}
                label="Project Group Notes"
                name={"projectDescription"}
                control={control}
                description={
                  "Notes are for internal use only and will not appear on outward-facing materials."
                }
              />
            </div>
          </div>
        </ProjectInfoFormStyled>
      );
    }

    if (isGroupDetailView) {
      return (
        <ProjectInfoFormStyled isGroupDetailView={isGroupDetailView}>
          <div className="grid-template">
            <div className="left-content-group">
              <div className="grid-2">
                <TextInputFormField
                  isRequired
                  label={isGrouped ? "Project Group Name" : "Project Name"}
                  name={"projectName"}
                  control={control}
                  defaultValue={formData?.projectName}
                />

                <TextInputFormField
                  description="Total projected service and material value."
                  label="Total Project Group Value"
                  name={"serviceValue"}
                  value={formData?.serviceValue || ""}
                  control={control}
                  inputType={"text"}
                  InputProps={{
                    startAdornment: <div>$</div>,
                  }}
                />
              </div>

              <div className="grid-3">
                <SelectFormField
                  name="client"
                  label="Client"
                  control={control}
                  isRequired
                  placeholder="Please Select"
                  options={sortedClientOptions}
                  disabled={!!formData?.id}
                  // disabled
                />
                <SelectFormField
                  // isRequired
                  name="contact"
                  label="Client Contact"
                  control={control}
                  placeholder="Please Select"
                  options={clientContactOptions}
                  disabled={isDetailedView}
                />
                <SelectFormField
                  isRequired
                  name="projectLead"
                  label={isGrouped ? "Project Group Lead" : "Project Lead"}
                  control={control}
                  placeholder="Please Select"
                  options={parsedProjectLeadOptions}
                />
              </div>
            </div>
            <div className="notes">
              <TextInputFormField
                isRequired
                multiline
                description={
                  "Notes are for internal use only and will not appear on outward-facing materials."
                }
                rows={15}
                label={isGrouped ? "Project Group Notes" : "Project Notes"}
                name={"projectDescription"}
                control={control}
              />
            </div>
          </div>
        </ProjectInfoFormStyled>
      );
    }

    return null;
  };

  return (
    <ProjectInfoFormStyled
      isDetailedView={isDetailedView || isGroupDetailView}
      isCreationView={isCreationView}
      isCreationGroupView={isCreationGroupView}
    >
      <div className="project-cont">
        <div className="row-style">{renderFieldsBasedOnView()}</div>
      </div>
    </ProjectInfoFormStyled>
  );
};

export default ProjectInfoForm;
